import React, { useEffect, useState } from 'react'
import AICodeFinder from './AICodeFinder';
import { Card } from '@tremor/react';
import TextCodeModal from '../TextCoder/TextCodeModal';
import CategoryAddConfirmModal from './CategoryAddConfirmModal';
import SuccessPopup from '../PopupNotifications/SuccessPopup';
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { deleteAiCode, getAiCode, getAiCodes, requestAiCodeFinding, groupCodesIntoCategories } from '../../Requests/AICodeFinder';
import { createOrUpdateCodes } from '../../Requests/TextCoderRequests';
import { getTeamUuid } from '../../utils/cookies';
import ParticleLoader from "../ParticleLoader";
import { useTranslation } from 'react-i18next';
import NoCreditsModal from '../Billing/NoCreditsModal';
import { useMyContext } from '../ContextProviders/CreditsContextProvider';
import { generateUniqueColors } from '../../utils/colorUtils';

function AICodeView({ project, setUploadAudioModal, handleUpdateCode, handleAddNewCode, handleDeleteCode, textCodes }) {
    const [aiCodes, setAiCodes] = useState([]);
    const { credits, setCredits } = useMyContext();
    const [waitingCodes, setWaitingCodes] = useState(null);
    const [isAsking, setIsAsking] = useState(true);
    const { t } = useTranslation()
    const [collapsedCards, setCollapsedCards] = useState({});
    const [insufficientCredits, setInsufficientCredits] = useState(null);
    const [groupingData, setGroupingData] = useState({});
    const [isGrouping, setIsGrouping] = useState({});
    const [showGrouped, setShowGrouped] = useState({});
    const [createdParentCodes, setCreatedParentCodes] = useState({});
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [selectedCategoryData, setSelectedCategoryData] = useState(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const res = await getAiCodes({ "project_uuid": project.uuid, "team_uuid": getTeamUuid() });
            
            const temp_res = [];
            const waiting_for = [];
            const initialGroupingData = {};
            const initialShowGrouped = {};

            res.data.forEach((coder_block, index) => {
                if (coder_block.status === "Processed") {
                    try {
                        let codesFound;
                        // Check if it's already an object (Laravel sometimes auto-decodes JSON)
                        if (typeof coder_block.codes_found === 'object') {
                            codesFound = coder_block.codes_found;
                        } else if (typeof coder_block.codes_found === 'string') {
                            codesFound = JSON.parse(coder_block.codes_found);
                        } else {
                            throw new Error(`Unexpected codes_found type: ${typeof coder_block.codes_found}`);
                        }
                        
                        // Validate the structure
                        if (!codesFound || typeof codesFound !== 'object') {
                            throw new Error('codes_found is not a valid object');
                        }
                        
                        const resultObject = { "aiCoder": coder_block, "result": codesFound };
                        temp_res.push(resultObject);
                        
                        // Load saved grouping data if it exists
                        if (coder_block.grouping_data) {
                            // Reconstruct the same data structure as the API response
                            initialGroupingData[coder_block.uuid] = {
                                grouping: coder_block.grouping_data,
                                original_codes: codesFound.codes
                            };
                            initialShowGrouped[coder_block.uuid] = false; // Default to hidden, user can toggle
                        }
                    } catch (parseError) {
                        console.error(`JSON parse error for block ${index}:`, parseError);
                        // Skip this block but continue processing others
                        return;
                    }
                } else if (coder_block.status === "Failed") {
                    temp_res.push(coder_block);
                }
                else {
                    waiting_for.push(coder_block);
                }
            });

            if (temp_res.length > 0) {
                setAiCodes(temp_res);
            } else {
                setAiCodes([]); // Explicitly set empty array
            }
            if (waiting_for.length > 0) {
                setWaitingCodes(waiting_for);
            }
            if (waiting_for.length < 1) {
                setIsAsking(false);
            }
            if (temp_res.length == 0 && waiting_for.length == 0) {
                setIsAsking(false);
            }
            
            // Set initial grouping data and show states
            setGroupingData(initialGroupingData);
            setShowGrouped(initialShowGrouped);
        };

        fetchData().catch(error => {
            console.error('Error in fetchData:', error);
        });
    }, [project.uuid]);




    useEffect(() => {
        const interval = setInterval(async () => {
            if (waitingCodes && waitingCodes.length > 0) {
                let allFailed = true; // Assume all codes have failed initially
                for (const waitingCode of waitingCodes) {
                    if (waitingCode.status != "Failed") {
                        const res = await getAiCode({ "project_uuid": project.uuid, "team_uuid": getTeamUuid(), "uuid": waitingCode.uuid });

                        if (res.data.status === "Processed") {
                            setAiCodes(prev => [{ "aiCoder": waitingCode, "result": JSON.parse(res.data.codes_found) }, ...prev]);
                            setWaitingCodes(prev => prev.filter(code => code.uuid !== waitingCode.uuid));
                            if (waitingCodes.length === 1) setIsAsking(false);
                        } else if (res.data.status === "Failed") {
                            allFailed = true; // If any code is not failed, update the flag

                            setAiCodes(prev => [res.data, ...prev]);
                            setWaitingCodes(prev => prev.filter(code => code.uuid !== waitingCode.uuid));
                        } else {
                            allFailed = false;
                        }
                    }
                }
                if (allFailed) {
                    clearInterval(interval); // Clear interval if all codes have failed
                    setIsAsking(false);
                }
            } else {
                clearInterval(interval);
            }
        }, 5000); // Check every 5 seconds.

        return () => clearInterval(interval);
    }, [waitingCodes, project.uuid]);




    const toggleCard = (uuid) => {
        setCollapsedCards(prev => ({
            ...prev,
            [uuid]: !prev[uuid]
        }));
    };

    const delete_code = (deleteCode, ifFailed = false) => {
        deleteAiCode({ "project_uuid": project.uuid, "team_uuid": getTeamUuid(), "uuid": deleteCode.uuid }).then((res) => {
            // Filter out the deleted code from the aiCodes state
            if (ifFailed) {
                setAiCodes(prev => prev?.filter(code => code.uuid !== deleteCode.uuid));
            } else {
                setAiCodes(prev => prev?.filter(code => code?.aiCoder?.uuid !== deleteCode.uuid));
                // Optionally filter out from waitingCodes state if needed
                setWaitingCodes(prev => prev?.filter(code => code.uuid !== deleteCode.uuid));
            }
        }).catch((e) => { })
    }

    const requestCodeFind = (data, cost) => {
        setIsAsking(true)
        requestAiCodeFinding(data).then((res) => {
            setWaitingCodes(prev => {
                setCredits(credits - cost)
                const updatedCodes = [res.data, ...(prev || [])];
                return updatedCodes;
            });
        }).catch((e) => {
            if (JSON.parse(e.request.response)["message"] == "Insufficient credits") {
                setInsufficientCredits(<NoCreditsModal
                    title={t('CreditsModal.Title')}
                    body={t('CreditsModal.Body')}
                    continue={t('CreditsModal.Continue')}
                    navLink="/pricing/credits"
                    handleClose={() => { setInsufficientCredits(null) }} />)
            }
            setIsAsking(false)
        })
    }

    const handleGroupCodes = (aiCodeFinderUuid) => {
        console.log('Starting code grouping for:', aiCodeFinderUuid);
        setIsGrouping(prev => ({ ...prev, [aiCodeFinderUuid]: true }));
        
        groupCodesIntoCategories({
            "project_uuid": project.uuid,
            "team_uuid": getTeamUuid(),
            "ai_code_finder_uuid": aiCodeFinderUuid
        }).then((res) => {
            console.log('Grouping successful:', res.data);
            setGroupingData(prev => ({ ...prev, [aiCodeFinderUuid]: res.data }));
            setShowGrouped(prev => ({ ...prev, [aiCodeFinderUuid]: true }));
            setIsGrouping(prev => ({ ...prev, [aiCodeFinderUuid]: false }));
        }).catch((e) => {
            console.error('Error grouping codes:', e);
            if (e.code === 'ECONNABORTED') {
                console.error('Request timed out after 3 minutes');
                alert('The grouping request timed out. Please try again or contact support if the issue persists.');
            } else if (e.response) {
                console.error('Server error:', e.response.status, e.response.data);
                alert(`Server error: ${e.response.data?.message || 'Unknown error'}`);
            } else if (e.request) {
                console.error('Network error - no response received');
                alert('Network error. Please check your connection and try again.');
            } else {
                console.error('Request setup error:', e.message);
                alert('An error occurred while setting up the request.');
            }
            setIsGrouping(prev => ({ ...prev, [aiCodeFinderUuid]: false }));
        });
    }

    const handleParentCodeCreated = (aiCodeFinderUuid, categoryName, parentCodeUuid) => {
        setCreatedParentCodes(prev => ({
            ...prev,
            [`${aiCodeFinderUuid}-${categoryName}`]: parentCodeUuid
        }));
    }

    const handleAddParentOnly = async () => {
        const { categoryName, categoryData, aiCodeFinderUuid } = selectedCategoryData;
        
        // Create the parent category
        const parentCodeData = {
            name: categoryName,
            description: categoryData.description,
            reasoning: categoryData.reasoning,
            project_id: project.uuid,
            color: generateUniqueColors(1, textCodes?.map(c => c.color) || [])[0],
            parent: null
        };

        try {
            const response = await createOrUpdateCodes(parentCodeData);
            handleAddNewCode(response.data);
            handleParentCodeCreated(aiCodeFinderUuid, categoryName, response.data.uuid);
            setShowSuccessPopup(true);
        } catch (error) {
            console.error('Error creating parent category:', error);
            throw error;
        }
    };

    const handleAddWithChildren = async () => {
        const { categoryName, categoryData, aiCodeFinderUuid, originalCodes } = selectedCategoryData;
        
        // Get existing colors to avoid duplicates
        const existingColors = textCodes?.map(c => c.color) || [];
        const neededColors = generateUniqueColors(1 + categoryData.subcodes.length, existingColors);
        
        try {
            // First create the parent category
            const parentCodeData = {
                name: categoryName,
                description: categoryData.description,
                reasoning: categoryData.reasoning,
                project_id: project.uuid,
                color: neededColors[0],
                parent: null
            };

            const parentResponse = await createOrUpdateCodes(parentCodeData);
            const parentUuid = parentResponse.data.uuid;
            
            // Add the parent to our state
            handleAddNewCode(parentResponse.data);
            handleParentCodeCreated(aiCodeFinderUuid, categoryName, parentUuid);

            // Then create all child categories
            for (let i = 0; i < categoryData.subcodes.length; i++) {
                const subcodeName = categoryData.subcodes[i];
                const subcodeData = originalCodes[subcodeName];
                const description = typeof subcodeData === 'string' ? subcodeData : subcodeData?.description || '';
                const reasoning = typeof subcodeData === 'object' ? subcodeData?.reasoning || '' : '';

                const childCodeData = {
                    name: subcodeName,
                    description: description,
                    reasoning: reasoning,
                    project_id: project.uuid,
                    color: neededColors[i + 1], // Use next color in the array
                    parent: parentUuid
                };

                const childResponse = await createOrUpdateCodes(childCodeData);
                handleAddNewCode(childResponse.data);
            }
            setShowSuccessPopup(true);
        } catch (error) {
            console.error('Error creating category with children:', error);
            throw error;
        }
    };

    const openCategoryConfirmModal = (categoryName, categoryData, aiCodeFinderUuid, originalCodes) => {
        setSelectedCategoryData({
            categoryName,
            categoryData,
            aiCodeFinderUuid,
            originalCodes
        });
        setConfirmModalOpen(true);
    };


    return <div data-testid="ai-code-view-container">
        <AICodeFinder project={project} isAsking={isAsking} setIsAsking={setIsAsking} requestCodeFind={requestCodeFind} />
        {aiCodes && (
            <>
                <div className='flex flex-col w-full gap-2 my-2' data-testid="ai-code-waiting-section">
                    {waitingCodes?.map((current_waiting_for) => {
                        return (<div data-testid={`ai-code-waiting-card-${current_waiting_for.uuid}`} key={current_waiting_for.uuid}>
                            <Card>
                                <div className='flex flex-col gap-4 '>
                                    <div className='font-bold '>
                                        {t('AiCodeDiscovery.FindingCodes')}
                                    </div>
                                    <ParticleLoader size={32} />
                                </div>
                            </Card>
                        </div>
                        )
                    })}
                </div>
                <div data-testid="ai-code-results-section">
                    {aiCodes?.map((current_code, mapIndex) => {
                        return (
                            current_code.status != "Failed" ?
                                <div data-testid={`ai-code-result-card-${current_code.aiCoder.uuid}`} key={current_code.aiCoder.uuid}>
                                    <Card className='my-2'>
                                        <div className='flex flex-col gap-4 mt0'>
                                            <div className='flex flex-col gap-2'>
                                                <div className='flex justify-between'>
                                                    <div className='font-bold '>{t('AiCodeDiscovery.Results')}</div>
                                                    <div className='flex gap-2 items-center'>
                                                        {!showGrouped[current_code.aiCoder.uuid] && !groupingData[current_code.aiCoder.uuid] && (
                                                            <button 
                                                                className='rounded-lg px-3 py-1 text-xs bg-amber-500 text-plate-ink hover:bg-amber-700 disabled:opacity-50'
                                                                disabled={isGrouping[current_code.aiCoder.uuid]}
                                                                onClick={() => handleGroupCodes(current_code.aiCoder.uuid)}
                                                                data-testid={`ai-code-group-button-${current_code.aiCoder.uuid}`}
                                                            >
                                                                {isGrouping[current_code.aiCoder.uuid] ? t('AiCodeDiscovery.GroupingCodes') : t('AiCodeDiscovery.GroupCodesButton')}
                                                            </button>
                                                        )}
                                                        {!showGrouped[current_code.aiCoder.uuid] && groupingData[current_code.aiCoder.uuid] && (
                                                            <button 
                                                                className='rounded-lg px-3 py-1 text-xs bg-green-600 text-white hover:bg-green-700'
                                                                onClick={() => setShowGrouped(prev => ({ ...prev, [current_code.aiCoder.uuid]: true }))}
                                                                data-testid={`ai-code-show-grouped-button-${current_code.aiCoder.uuid}`}
                                                            >
                                                                {t('AiCodeDiscovery.ShowGrouped', 'Show Grouped')}
                                                            </button>
                                                        )}
                                                        {showGrouped[current_code.aiCoder.uuid] && (
                                                            <button 
                                                                className='rounded-lg px-3 py-1 text-xs bg-gray-600 text-white hover:bg-gray-700'
                                                                onClick={() => setShowGrouped(prev => ({ ...prev, [current_code.aiCoder.uuid]: false }))}
                                                                data-testid={`ai-code-show-original-button-${current_code.aiCoder.uuid}`}
                                                            >
                                                                {t('AiCodeDiscovery.ShowOriginal', 'Show Original')}
                                                            </button>
                                                        )}
                                                        <button onClick={() => toggleCard(current_code.aiCoder.uuid)} data-testid={`ai-code-toggle-${current_code.aiCoder.uuid}`}>
                                                            {collapsedCards[current_code.aiCoder.uuid] ? <ChevronDownIcon className='w68 h-6' /> : <ChevronUpIcon className='w-6 h-6' />}
                                                        </button>
                                                    </div>

                                                </div>
                                                <div className='flex text-xs gap-2 '>
                                                    {t('TextCoder.Files')}:
                                                    {project?.analysis?.map(current_analysis => {
                                                        const analysisUUIDs = current_code?.aiCoder?.analysis_uuids.split(',');
                                                        if (analysisUUIDs.includes(current_analysis?.uuid)) {
                                                            return <div>{current_analysis?.name}</div>
                                                        }
                                                        return null
                                                    })}
                                                </div>
                                            </div>
                                            {!collapsedCards[current_code.aiCoder.uuid] && (
                                                <>
                                                    {/* Show grouped view if available */}
                                                    {showGrouped[current_code.aiCoder.uuid] && groupingData[current_code.aiCoder.uuid] ? (
                                                        <div className='flex flex-col rounded-md py-2' data-testid={`ai-code-grouped-view-${current_code.aiCoder.uuid}`}>
                                                            {/* Parent Categories */}
                                                            {Object.entries(groupingData[current_code.aiCoder.uuid].grouping?.parent_categories || {}).map(([categoryName, categoryData], categoryIndex) => (
                                                                <div key={categoryName} className="flex justify-start flex-col mb-6 rounded-lg border border-gray-300 p-4 gap-3 bg-white">
                                                                    <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-2' data-testid={`ai-code-parent-category-${current_code.aiCoder.uuid}-${categoryIndex}`}>
                                                                        <div className='font-bold text-lg text-gray-900'>
                                                                            {t('AiCodeDiscovery.ParentCategory')}: {categoryName}
                                                                        </div>
                                                                        <button 
                                                                            className='rounded-lg px-3 py-1 text-xs bg-black text-white hover:bg-gray-800 w-full md:w-fit'
                                                                            onClick={() => openCategoryConfirmModal(
                                                                                categoryName,
                                                                                categoryData,
                                                                                current_code.aiCoder.uuid,
                                                                                groupingData[current_code.aiCoder.uuid].original_codes
                                                                            )}
                                                                            data-testid={`ai-code-add-category-button-${current_code.aiCoder.uuid}-${categoryIndex}`}
                                                                        >
                                                                            {t('AiCodeDiscovery.AddCategory', 'Add category')}
                                                                        </button>
                                                                    </div>
                                                                    <div className='text-sm mb-2'>
                                                                        <strong>{t('TextCoder.Code description')}:</strong> {categoryData.description}
                                                                    </div>
                                                                    <div className='text-sm text-gray-700 mb-3'>
                                                                        <strong>{t('AiCodeDiscovery.CategoryReasoning')}:</strong> {categoryData.reasoning}
                                                                    </div>
                                                                    
                                                                    {/* Subcodes */}
                                                                    <div className='border-t pt-3'>
                                                                        <div className='font-semibold text-sm mb-2'>{t('AiCodeDiscovery.Subcodes')}:</div>
                                                                        <div className='grid gap-2'>
                                                                            {categoryData.subcodes?.map((subcodeName, subcodeIndex) => {
                                                                                const subcodeData = groupingData[current_code.aiCoder.uuid].original_codes[subcodeName];
                                                                                const description = typeof subcodeData === 'string' ? subcodeData : subcodeData?.description || '';
                                                                                const reasoning = typeof subcodeData === 'object' ? subcodeData?.reasoning || '' : '';
                                                                                
                                                                                return (
                                                                                    <div key={subcodeName} className="flex justify-start flex-col ml-4 rounded-lg border border-gray-300 p-2 gap-2 bg-white">
                                                                                        <div className='flex flex-col md:flex-row justify-between items-start gap-2'>
                                                                                            <div className='font-medium text-sm'>{subcodeName}</div>
                                                                                            <button className='rounded-lg px-3 py-1 text-xs bg-black text-white hover:bg-gray-700 w-full md:w-fit'
                                                                                                onClick={() => {
                                                                                                    const parentCodeUuid = createdParentCodes[`${current_code.aiCoder.uuid}-${categoryName}`];
                                                                                                    setUploadAudioModal(
                                                                                                        <TextCodeModal 
                                                                                                            handleClose={() => setUploadAudioModal(null)} 
                                                                                                            handleUpdateCode={handleUpdateCode} 
                                                                                                            handleAddNewCode={handleAddNewCode} 
                                                                                                            handleDeleteCode={handleDeleteCode}
                                                                                                            textCodes={textCodes || []}
                                                                                                            code={{ 
                                                                                                                "name": subcodeName, 
                                                                                                                "description": description, 
                                                                                                                "reasoning": reasoning,
                                                                                                                "parent": parentCodeUuid || null
                                                                                                            }} 
                                                                                                        />
                                                                                                    );
                                                                                                }}>
                                                                                                {t('AiCodeDiscovery.AddCodeLabel')}
                                                                                            </button>
                                                                                        </div>
                                                                                        <div className='text-xs'>
                                                                                            <strong>{t('TextCoder.Code description')}:</strong> {description}
                                                                                        </div>
                                                                                        {reasoning && (
                                                                                            <div className='text-xs text-gray-600'>
                                                                                                <strong>{t('TextCoder.Code reasoning')}:</strong> {reasoning}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            
                                                            {/* Ungrouped Codes */}
                                                            {groupingData[current_code.aiCoder.uuid].grouping?.ungrouped_codes?.length > 0 && (
                                                                <div className="flex justify-start flex-col mb-4 rounded-lg border border-gray-400 p-4 gap-3 bg-gray-50">
                                                                    <div className='font-bold text-lg text-gray-700'>{t('AiCodeDiscovery.UngroupedCodes')}</div>
                                                                    <div className='grid gap-2'>
                                                                        {groupingData[current_code.aiCoder.uuid].grouping.ungrouped_codes.map((ungroupedCodeName, ungroupedIndex) => {
                                                                            const ungroupedData = groupingData[current_code.aiCoder.uuid].original_codes[ungroupedCodeName];
                                                                            const description = typeof ungroupedData === 'string' ? ungroupedData : ungroupedData?.description || '';
                                                                            const reasoning = typeof ungroupedData === 'object' ? ungroupedData?.reasoning || '' : '';
                                                                            
                                                                            return (
                                                                                <div key={ungroupedCodeName} className="flex justify-start flex-col rounded-lg border border-gray-300 p-2 gap-2 bg-white">
                                                                                    <div className='flex flex-col md:flex-row justify-between items-center gap-2'>
                                                                                        <div className='font-medium text-sm'>{ungroupedCodeName}</div>
                                                                                        <button className='rounded-lg px-3 py-1 text-xs bg-black text-white hover:bg-gray-700 w-full md:w-fit'
                                                                                            onClick={() => setUploadAudioModal(
                                                                                                <TextCodeModal 
                                                                                                    handleClose={() => setUploadAudioModal(null)} 
                                                                                                    handleUpdateCode={handleUpdateCode} 
                                                                                                    handleAddNewCode={handleAddNewCode} 
                                                                                                    handleDeleteCode={handleDeleteCode}
                                                                                                    textCodes={textCodes || []}
                                                                                                    code={{ "name": ungroupedCodeName, "description": description, "reasoning": reasoning }} 
                                                                                                />
                                                                                            )}>
                                                                                            {t('AiCodeDiscovery.AddCodeLabel')}
                                                                                        </button>
                                                                                    </div>
                                                                                    <div className='text-xs'>
                                                                                        <strong>{t('TextCoder.Code description')}:</strong> {description}
                                                                                    </div>
                                                                                    {reasoning && (
                                                                                        <div className='text-xs text-gray-600'>
                                                                                            <strong>{t('TextCoder.Code reasoning')}:</strong> {reasoning}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        /* Original view */
                                                        <div className='flex flex-col rounded-md py-2' data-testid={`ai-code-discovered-codes-${current_code.aiCoder.uuid}`}>
                                                            {Object.entries(current_code.result?.codes || {}).map(([key, value], index) => {
                                                                // Handle both old format (string value) and new format (object with description/reasoning)
                                                                const description = typeof value === 'string' ? value : value?.description || '';
                                                                const reasoning = typeof value === 'object' ? value?.reasoning || '' : '';
                                                                
                                                                return (
                                                                    <div className="flex justify-start flex-col mb-4 rounded-lg border border-black p-2 gap-2" key={key} data-testid={`ai-code-discovered-code-${current_code.aiCoder.uuid}-${index}`}>
                                                                        <div className='flex flex-col md:flex-row justify-between items-center gap-2'>
                                                                            <div className='font-bold' data-testid={`ai-code-name-${current_code.aiCoder.uuid}-${index}`}>{key}</div>
                                                                            <div className='flex justify-center items-center w-full md:w-fit'>
                                                                                <button className='rounded-lg px-4 py-1 text-md bg-black text-white hover:bg-white hover:text-black w-full md:w-fit'
                                                                                    data-testid={`ai-code-add-button-${current_code.aiCoder.uuid}-${index}`}
                                                                                    onClick={() => setUploadAudioModal(
                                                                                        <TextCodeModal 
                                                                                            handleClose={() => setUploadAudioModal(null)} 
                                                                                            handleUpdateCode={handleUpdateCode} 
                                                                                            handleAddNewCode={handleAddNewCode} 
                                                                                            handleDeleteCode={handleDeleteCode}
                                                                                            textCodes={textCodes || []}
                                                                                            code={{ "name": key, "description": description, "reasoning": reasoning }} 
                                                                                        />
                                                                                    )}>
                                                                                    {t('AiCodeDiscovery.AddCodeLabel')}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div className='text-sm mb-2' data-testid={`ai-code-description-${current_code.aiCoder.uuid}-${index}`}>
                                                                            <strong>{t('TextCoder.Code description')}:</strong> {description}
                                                                        </div>
                                                                        {reasoning && (
                                                                            <div className='text-sm text-gray-700' data-testid={`ai-code-reasoning-${current_code.aiCoder.uuid}-${index}`}>
                                                                                <strong>{t('TextCoder.Code reasoning')}:</strong> {reasoning}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                            <div className='flex flex-col rounded-md px-4 py-4' key={current_code.aiCoder.uuid}>
                                                <div className='text-lg font-bold mb-4'>
                                                    {t('AiCodeDiscovery.TableTitle')}
                                                </div>
                                                {/* Dynamically adjusting grid columns based on the number of interviews, adding fallback */}
                                                <div className={`grid items-center justify-center grid-cols-${Object.keys(current_code?.result?.interviews || {}).length + 1}`}>
                                                    <div className=''>
                                                        {t('AiCodeDiscovery.TableCorner')}

                                                    </div>
                                                    {Object.keys(current_code.result?.interviews || {}).map((interviewKey) => (
                                                        <div className='font-bold text-center border-l-2  my-1'>{interviewKey}</div>
                                                    ))}
                                                    {Object.entries(current_code.result?.codes || {}).map(([key, value], index) => (
                                                        <React.Fragment key={index}>
                                                            <div className='font-bold text-sm h-full border-t-2 my-1'>{key}</div>
                                                            {Object.entries(current_code.result?.interviews || {}).map(([interviewKey, InterviewValue]) => (
                                                                <div className="flex w-full justify-center gap-8 border-l-2 border-t-2 h-full items-center" key={interviewKey}>
                                                                    <div className={"h-fit rounded-md " + ((InterviewValue[key] == true || InterviewValue[key] == "true") ? "bg-lime-700" : "bg-red-700")}>
                                                                        {(InterviewValue[key] == true || InterviewValue[key] == "true") ? <CheckIcon className='text-white w-5 h-5' /> : <XMarkIcon className='text-white w-5 h-5' />}
                                                                    </div>
                                                                </div>
                                                            ))}

                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                                    <div className='flex justify-end'>
                                                        <button className='bg-red-800 rounded-md px-4 py-2 text-white w-fit'
                                                            onClick={() => { delete_code(current_code.aiCoder, false) }}
                                                            data-testid={`ai-code-delete-button-${current_code.aiCoder.uuid}`}
                                                        >{t('AiCodeDiscovery.Delete')}</button>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                    </Card>
                                </div> :
                                <div data-testid={`ai-code-failed-card-${current_code.uuid}`} key={current_code.uuid}>
                                    <Card className='my-2'>
                                        <div className='flex items-center justify-between gap-4 '>
                                            <div className='font-bold text-red-600'>
                                                {t('AiCodeDiscovery.Error')}
                                            </div>
                                            <button className='bg-red-800 rounded-md px-4 py-2 text-white'
                                                onClick={() => { delete_code(current_code, true) }}
                                                data-testid={`ai-code-failed-delete-button-${current_code.uuid}`}
                                            >{t('AiCodeDiscovery.Delete')}</button>
                                        </div>
                                    </Card>
                                </div>
                        )
                    })}
                </div>

            </>
        )}

        {insufficientCredits}
        
        {/* Category Add Confirmation Modal */}
        {selectedCategoryData && (
            <CategoryAddConfirmModal
                isOpen={confirmModalOpen}
                onClose={() => {
                    setConfirmModalOpen(false);
                    setSelectedCategoryData(null);
                }}
                categoryName={selectedCategoryData.categoryName}
                subcodes={selectedCategoryData.categoryData?.subcodes}
                onAddParentOnly={handleAddParentOnly}
                onAddWithChildren={handleAddWithChildren}
            />
        )}
        
        {/* Success Popup */}
        {showSuccessPopup && (
            <SuccessPopup
                text={t('AiCodeDiscovery.CategoriesAdded', 'Categorias adicionadas')}
                handleClose={() => setShowSuccessPopup(false)}
            />
        )}
    </div>;
}



export default AICodeView
